/**
 * /api/orgs/:orgId/share-profile — what is opened into this Workspace.
 *
 * SPEC-11 / W3-18. Every route 404s unless the gateway's
 * SHARE_PROFILES_ENABLED is on, so `useShareProfile` treats a 404 as
 * "not enabled on this instance" and the card renders an honest line rather
 * than an error boundary. Reads are open to any Workspace member; writes are
 * refused by the gateway for anyone but the owner of the account the Workspace
 * belongs to.
 */
import { ApiError, apiJson } from './client';

export type SharePosture = 'closed' | 'inherit_all';

export type ShareSurface =
  | 'role'
  | 'skill'
  | 'connector'
  | 'permission'
  | 'template'
  | 'approvalRule'
  | 'credential';

export interface ShareProfile {
  id: string;
  orgId: string;
  accountId: string | null;
  posture: SharePosture;
  source: 'backfill' | 'workspace_create' | 'owner_edit';
  createdAt: string;
  updatedAt: string;
}

export interface ShareProfileEntry {
  surface: ShareSurface;
  itemKey: string;
  label: string;
  grantedVia: string;
  grantedAt: string | null;
}

export interface ShareSurfaceSummary {
  surface: ShareSurface;
  open: number;
  explicit: number;
  automatic: number;
  writable: boolean;
}

export interface ShareBaselineRow {
  surface: ShareSurface;
  openCount: number;
  capturedAt: string | null;
}

export interface ShareProfileResponse {
  profile: ShareProfile;
  surfaces: ShareSurfaceSummary[];
  entries: ShareProfileEntry[];
  baseline: ShareBaselineRow[];
}

export const shareProfilesApi = {
  get(orgId: string): Promise<ShareProfileResponse> {
    return apiJson<ShareProfileResponse>(`/orgs/${orgId}/share-profile`);
  },
  setPosture(orgId: string, posture: SharePosture): Promise<{ profile: ShareProfile }> {
    return apiJson<{ profile: ShareProfile }>(`/orgs/${orgId}/share-profile`, {
      method: 'PUT',
      json: { posture },
    });
  },
  open(
    orgId: string,
    surface: ShareSurface,
    itemKeys: string[],
  ): Promise<{ opened: number; failedMarks: number }> {
    return apiJson(`/orgs/${orgId}/share-profile/entries`, {
      method: 'POST',
      json: { surface, itemKeys },
    });
  },
  close(
    orgId: string,
    surface: ShareSurface,
    itemKeys: string[],
  ): Promise<{ closed: number; failedMarks: number }> {
    return apiJson(`/orgs/${orgId}/share-profile/entries`, {
      method: 'DELETE',
      json: { surface, itemKeys },
    });
  },
};

/**
 * IS THE MECHANISM SWITCHED ON AT ALL, as read off a failed request.
 *
 * Every route in this file answers 404 `feature_disabled` while the gateway's
 * SHARE_PROFILES_ENABLED is off, so a 404 on the READ is the only honest signal
 * a screen has: it means "this server does not keep what is open here", not
 * "nothing is open here". The distinction decides whether a screen may show a
 * list at all -- an empty list would read as "everything is switched off", and
 * on an installation where the flag is off exactly nothing is switched off.
 *
 * Written once, here, because three components now ask the question and three
 * inline `err instanceof ApiError && err.status === 404` are three chances for
 * the fourth one to be forgotten.
 */
export function shareMechanismIsOff(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export const shareProfilesKeys = {
  all: ['share-profile'] as const,
  byOrg: (orgId: string) => [...shareProfilesKeys.all, orgId] as const,
};

/** Human labels for the seven surfaces — the card's only copy table. */
export const SHARE_SURFACE_LABEL: Record<ShareSurface, string> = {
  role: 'Roles',
  skill: 'Skills',
  connector: 'Connectors',
  permission: 'Permissions',
  template: 'Templates',
  approvalRule: 'Automation rules',
  // A tab label. The card shows credential NAMES and never a value; every
  // credential value stays behind credentials.ts and its vault fences.
  // chaos-secret-scan: allow -- the string 'Credentials' is a tab label
  credential: 'Credentials',
};
